import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateExpense } from '@/hooks/useExpenses'
import { useExpenseCategories } from '@/hooks/useAccounting'
import { useEmployees } from '@/hooks/useEmployees'
import { useRecurringExpenses } from '@/hooks/useRecurringExpenses'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { fr, arSA } from 'date-fns/locale'
import { cn } from '@/lib/utils'

interface AddExpenseDialogProps {
  restaurantId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  // Pre-fill options
  employeeId?: string
  recurringExpenseId?: string
}

export function AddExpenseDialog({
  restaurantId,
  open,
  onOpenChange,
  onSuccess,
  employeeId: prefillEmployeeId,
  recurringExpenseId: prefillRecurringId,
}: AddExpenseDialogProps) {
  const { t, i18n } = useTranslation()
  const { data: categories } = useExpenseCategories(restaurantId)
  const { data: employees } = useEmployees(restaurantId)
  const { data: recurringExpenses } = useRecurringExpenses(restaurantId)
  const createExpenseMutation = useCreateExpense(restaurantId)

  const isSubmittingRef = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Find prefilled data
  const prefillEmployee = employees?.find(e => e.id === prefillEmployeeId)
  const prefillRecurring = recurringExpenses?.find(r => r.id === prefillRecurringId)

  // Get salary category for employee payments
  const salaryCategory = categories?.find(c => c.icon === 'users' && c.isSystem)

  const [formData, setFormData] = useState({
    categoryId: prefillRecurring?.categoryId || salaryCategory?.id || '',
    expenseDate: new Date(),
    amount: prefillEmployee?.monthlySalary || prefillRecurring?.amount || 0,
    descriptionAr: prefillEmployee
      ? `${t('accounting.salaryPayment')} - ${prefillEmployee.nameAr}`
      : prefillRecurring?.nameAr || '',
    descriptionFr: prefillEmployee
      ? `${t('accounting.salaryPayment')} - ${prefillEmployee.nameFr}`
      : prefillRecurring?.nameFr || '',
    employeeId: prefillEmployeeId || '',
    recurringExpenseId: prefillRecurringId || '',
    referenceNumber: '',
    notes: '',
  })

  const locale = i18n.language === 'ar' ? arSA : fr
  const isRTL = i18n.language === 'ar'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    setErrors({})

    const newErrors: Record<string, string> = {}
    if (!formData.categoryId) {
      newErrors['categoryId'] = t('validation.required')
    }
    if (!formData.expenseDate) {
      newErrors['expenseDate'] = t('validation.required')
    }
    if (!formData.amount || formData.amount <= 0) {
      newErrors['amount'] = t('validation.required')
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      isSubmittingRef.current = false
      setIsSubmitting(false)
      return
    }

    try {
      const payload: {
        categoryId: string
        expenseDate: string
        amount: number
        descriptionAr?: string
        descriptionFr?: string
        employeeId?: string
        recurringExpenseId?: string
        referenceNumber?: string
        notes?: string
      } = {
        categoryId: formData.categoryId,
        expenseDate: format(formData.expenseDate, 'yyyy-MM-dd'),
        amount: formData.amount,
      }
      if (formData.descriptionAr) payload.descriptionAr = formData.descriptionAr
      if (formData.descriptionFr) payload.descriptionFr = formData.descriptionFr
      if (formData.employeeId) payload.employeeId = formData.employeeId
      if (formData.recurringExpenseId) payload.recurringExpenseId = formData.recurringExpenseId
      if (formData.referenceNumber) payload.referenceNumber = formData.referenceNumber
      if (formData.notes) payload.notes = formData.notes

      await createExpenseMutation.mutateAsync(payload)
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      setErrors({ form: error instanceof Error ? error.message : t('error.generic') })
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  // Filter categories to only show active ones
  const activeCategories = categories?.filter(c => c.isActive) || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] md:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('accounting.addExpense')}</DialogTitle>
            <DialogDescription>{t('accounting.addExpenseDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">{t('accounting.category')} *</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
              >
                <SelectTrigger className={errors['categoryId'] ? 'border-destructive' : ''}>
                  <SelectValue placeholder={t('accounting.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {activeCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      <span style={{ color: category.color }}>
                        {i18n.language === 'ar' ? category.nameAr : category.nameFr}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors['categoryId'] && (
                <p className="text-xs text-destructive">{errors['categoryId']}</p>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>{t('accounting.date')} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.expenseDate && 'text-muted-foreground',
                      errors['expenseDate'] && 'border-destructive'
                    )}
                  >
                    <CalendarIcon className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                    {formData.expenseDate ? (
                      format(formData.expenseDate, 'PPP', { locale })
                    ) : (
                      <span>{t('common.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.expenseDate}
                    onSelect={(date) => date && setFormData({ ...formData, expenseDate: date })}
                    initialFocus
                    locale={locale}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </PopoverContent>
              </Popover>
              {errors['expenseDate'] && (
                <p className="text-xs text-destructive">{errors['expenseDate']}</p>
              )}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">{t('accounting.amount')} ({t('common.currency')}) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount === 0 ? '' : formData.amount}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData({ ...formData, amount: isNaN(value) ? 0 : value });
                }}
                placeholder="0.00"
                className={errors['amount'] ? 'border-destructive' : ''}
              />
              {errors['amount'] && (
                <p className="text-xs text-destructive">{errors['amount']}</p>
              )}
            </div>

            {/* Description AR */}
            <div className="space-y-2">
              <Label htmlFor="descriptionAr">{t('accounting.description')} (AR)</Label>
              <Input
                id="descriptionAr"
                value={formData.descriptionAr}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                dir="rtl"
              />
            </div>

            {/* Description FR */}
            <div className="space-y-2">
              <Label htmlFor="descriptionFr">{t('accounting.description')} (FR)</Label>
              <Input
                id="descriptionFr"
                value={formData.descriptionFr}
                onChange={(e) => setFormData({ ...formData, descriptionFr: e.target.value })}
              />
            </div>

            {/* Reference Number */}
            <div className="space-y-2">
              <Label htmlFor="referenceNumber">{t('accounting.reference')}</Label>
              <Input
                id="referenceNumber"
                value={formData.referenceNumber}
                onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                placeholder={t('accounting.referencePlaceholder')}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('accounting.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
